import styled from "@emotion/styled";
import { usePrevious } from "@react-hookz/web";
import { ThemeKey } from "@smartsheet/design-tokens";
import { genId } from "@smartsheet/lodestar-utils";
import { getClientId } from "@smartsheet/lodestar-utils";
import { RequiredThemeKeys } from "@smartsheet/sds-theme-internal";
import { UseMultipleSelectionActions, UseMultipleSelectionPropGetters } from "downshift";
import React, {
    HTMLAttributes,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    KeyboardEvent,
    MouseEvent,
} from "react";
import { useI18n } from "../../../../i18n/i18n";
import { useTheme } from "../../../../theme/utils/useTheme";
import { getInputFieldHeightValue, InputFieldBaseProps } from "../../../../util/inputFieldBaseProps";
import { AvatarProps } from "../../../avatar";
import { AvatarChip, Chip } from "../../../chip";
import { chipTheme } from "../../../chip/styled/chipTheme";
import { ChipColorVariant } from "../../../chip/types";
import { BaseOptionType, FormatMultiSelectValue, RenderValueProps } from "../../types";
import { stopPropagationHandler, getChipColor } from "../../utils";
import { selectV2Theme } from "../selectV2Theme";

interface MultiSelectValueContainerStyledProps extends InputFieldBaseProps {
    maxHeight: number;
    themeMode: RequiredThemeKeys;
    minHeight?: number;
}

const MultiSelectValueContainerStyled = styled("div")<MultiSelectValueContainerStyledProps>`
    padding-top: var(--sds--multiselectv2--value-container--padding-top, 6px);
    padding-right: var(
        --sds--multiselectv2--value-container--padding-right,
        0px
    ); //right padding is actually handled by the actions div margin-right
    padding-bottom: var(--sds--multiselectv2--value-container--padding-bottom, 6px);
    padding-left: var(--sds--multiselectv2--value-container--padding-left, 6px);
    flex-grow: 1;
    box-sizing: border-box;
    max-height: ${({ maxHeight }) => maxHeight}px;
    min-height: ${({ minHeight }) => minHeight}px;
    align-content: flex-start;

    // For Chip eliding.
    min-width: 1px;
    overflow: auto;
    display: flex;

    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    // Make sure the Chips properly elide.
    overflow: auto;
`;

interface MultiSelectValueContainerProps<Option extends BaseOptionType>
    extends InputFieldBaseProps,
        Pick<UseMultipleSelectionPropGetters<Option>, "getSelectedItemProps">,
        Pick<UseMultipleSelectionActions<Option>, "removeSelectedItem" | "setSelectedItems" | "setActiveIndex"> {
    activeIndex: number;
    isReadonly: boolean;
    maxSelectedRows: number;
    numSelected: number;
    onChipNavigation: (direction: "tab" | "down" | "up") => void;
    onHeightChange: (newHeight: number) => void;
    optionToString: (option: Option) => string;
    rollupSelectionAfter: number | undefined;
    selectedItems: Option[];
    children?: ReactNode;
    className?: string;
    clientId?: string;
    formatSelectedValue?: FormatMultiSelectValue<Option>;
    id?: string;
    minHeight?: number;
    onChipClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const chipOverrides = { removeButton: { role: "note" } };

export const MultiSelectValueContainer = <Option extends BaseOptionType>({
    activeIndex,
    children,
    className,
    clientId,
    getSelectedItemProps,
    id,
    isReadonly = false,
    minHeight,
    maxSelectedRows,
    rollupSelectionAfter,
    numSelected,
    onHeightChange,
    optionToString,
    removeSelectedItem,
    selectedItems,
    setActiveIndex,
    setSelectedItems,
    onChipClick,
    onChipNavigation,
    formatSelectedValue,
    inputFieldHeight: inputFieldHeightProp,
    ...rest
}: MultiSelectValueContainerProps<Option>) => {
    const { i18n } = useI18n();
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const [chipIdPrefix] = useState(() => genId("lodestar-ms-chip"));
    const containerRef = useRef<HTMLDivElement>(null);

    const isInteractive = !isReadonly;

    const containerElement = containerRef.current;
    useEffect(() => {
        if (window.ResizeObserver && containerElement) {
            const observer = new ResizeObserver((entry) => {
                const newVerticalSize = entry[0]?.contentBoxSize[0]?.blockSize || 0;
                onHeightChange(newVerticalSize);
            });
            observer.observe(containerElement);
            return () => {
                observer.disconnect();
            };
        }
    }, [containerElement, onHeightChange]);

    /*
    if (maxRows === 1) show 1 row
    if (maxRows === 2) show 1.5 rows
    if (maxRows === 3) show 2.5 rows
    ... on and on ...
    */
    const inputFieldHeight = getInputFieldHeightValue(themeMode, inputFieldHeightProp);
    const chipHeightPX = chipTheme.height[selectV2Theme.chip.size[inputFieldHeight](themeMode)](themeMode);
    const chipHeight = parseInt(chipHeightPX);
    let maxHeight =
        chipHeight + 8 + (maxSelectedRows > 1 ? chipHeight / 2 + (maxSelectedRows - 2) * (chipHeight + 4) : 0);
    // TODO: This code assumes the rendered option is a Chip.  Refactor so caller can indicate rendered Option height
    //  when calculating number of rows to display.
    // Safety: make sure height isn't less than the inputFieldHeight
    maxHeight = Math.max(inputFieldHeight, maxHeight);

    // Only apply these props when we actually have children, otherwise the axe scanners
    // tell us that:
    // 1: a list box MUST have children.
    // 2: a div without a role shouldn't have a label.
    const includeAriaProps = numSelected > 0;
    const ariaProps = useMemo(
        () => ({
            ...(includeAriaProps
                ? {
                      "aria-label": i18n._(
                          /*i18n*/ {
                              comment: "This is a label referring to all the items the user has selected",
                              id: "Selected Items",
                          }
                      ),
                      role: "listbox",
                  }
                : undefined),
        }),
        [includeAriaProps, i18n]
    );

    const selectedItemsToShow = useMemo(
        () => (rollupSelectionAfter === undefined ? selectedItems : selectedItems.slice(0, rollupSelectionAfter)),
        [rollupSelectionAfter, selectedItems]
    );
    const selectedValue = selectedItems.length - selectedItemsToShow.length;

    const handleChipNavigation: React.KeyboardEventHandler = useCallback(
        (e) => {
            switch (e.key) {
                case "Tab":
                    //don't do custom input focusing on tab when tab is pressed w/ SHFT (backward keyboard tab stop nav)
                    if (e.shiftKey) {
                        // don't do e.preventDefault() here, or it won't ever let you backward tab
                        break;
                    } else {
                        e.preventDefault();
                        setActiveIndex(-1);
                        onChipNavigation("tab");
                        break;
                    }
                case "ArrowDown":
                    if (isInteractive) {
                        e.preventDefault();
                        onChipNavigation("down");
                    }
                    break;
                case "ArrowUp":
                    if (isInteractive) {
                        e.preventDefault();
                        onChipNavigation("up");
                    }
                    break;
            }
        },
        [isInteractive, onChipNavigation, setActiveIndex]
    );

    const selectionLength = selectedItems.length;
    const previousSelectionLength = usePrevious(selectionLength);
    const [hasFocus, setHasFocus] = useState(false);

    // If a chip previously had focus and the previously focused chip was deleted, we'll need
    // to move focus to a neighboring chip.
    useEffect(() => {
        if (hasFocus && previousSelectionLength !== selectionLength && activeIndex >= 0) {
            (Array.from(containerRef.current?.childNodes || []) as HTMLElement[])[activeIndex]?.focus();
        }
    }, [hasFocus, activeIndex, previousSelectionLength, selectionLength]);

    const handleChipOnRemove = useCallback(
        (e: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>, option: Option) => {
            e.stopPropagation();
            removeSelectedItem(option);
        },
        [removeSelectedItem]
    );

    // TODO: For performance I was trying to break this down into memoizable chunks but the dependency tree is huge.
    //  May want to try to create a new separate functional component so React can monitor the direct prop changes
    //  (which should be nothing during simple re-renders).
    const renderChip = (option: Option, index: number) => {
        const asString = optionToString(option);
        const chipId = id ? `${id}--selected-item-${index}` : `${chipIdPrefix}_${index}`;
        const chipClientId = getClientId(clientId, "-selected-item");

        const chipProps: RenderValueProps = {
            onRemove: isInteractive ? (e) => handleChipOnRemove(e, option) : undefined,
            // Do we need this still?
            isFocused: activeIndex === index,
            isReadonly,
            children: null,
            key: asString,
            clientId: chipClientId,
            ...(getSelectedItemProps({
                index,
                role: "option",
                selectedItem: option,
                id: chipId,
                onClick: onChipClick ? onChipClick : stopPropagationHandler,
                onKeyDown: handleChipNavigation,
            }) as HTMLAttributes<HTMLDivElement>),
        };

        if (formatSelectedValue) return formatSelectedValue(option, chipProps, false, 0);

        return option.avatarProps ? (
            // We're good. key is part of chipProps.
            // eslint-disable-next-line react/jsx-key
            <AvatarChip
                size={selectV2Theme.chip.size[inputFieldHeight](themeMode)}
                {...chipProps}
                avatarProps={option.avatarProps as AvatarProps}
                overrides={chipOverrides}
            >
                {asString}
            </AvatarChip>
        ) : (
            <Chip
                size={selectV2Theme.chip.size[inputFieldHeight](themeMode)}
                {...chipProps}
                color={getChipColor(option.color as ChipColorVariant)}
                overrides={chipOverrides}
            >
                {asString}
            </Chip>
        );
    };

    return (
        <MultiSelectValueContainerStyled
            maxHeight={maxHeight}
            minHeight={minHeight}
            ref={containerRef}
            onFocus={() => setHasFocus(true)}
            onBlur={() => setHasFocus(false)}
            className={className}
            {...ariaProps}
            themeMode={themeMode}
            data-client-id={`${clientId ?? ""}-value-container`}
            {...rest}
        >
            {selectedItemsToShow.map((option: Option, index: number) => {
                return renderChip(option, index);
            })}
            {selectedItemsToShow.length < selectedItems.length &&
                (formatSelectedValue !== undefined ? (
                    formatSelectedValue(
                        null,
                        {
                            onRemove: isInteractive
                                ? (e) => {
                                      e.stopPropagation();
                                      setActiveIndex(selectedItemsToShow.length - 1);
                                      setSelectedItems(selectedItemsToShow);
                                  }
                                : undefined,
                            children: null,
                            ...(getSelectedItemProps({
                                index: selectedItemsToShow.length,
                                role: "option",
                                selectedItem: undefined as unknown as Option,
                                id: `${chipIdPrefix}_${selectedItemsToShow.length}`,
                                onClick: onChipClick ? onChipClick : stopPropagationHandler,
                            }) as HTMLAttributes<HTMLDivElement>),
                        },
                        true,
                        selectedValue
                    )
                ) : (
                    <Chip
                        isReadonly={true}
                        onRemove={
                            isInteractive
                                ? (e) => {
                                      e.stopPropagation();
                                      setActiveIndex(selectedItemsToShow.length - 1);

                                      setSelectedItems(selectedItemsToShow);
                                  }
                                : undefined
                        }
                        {...getSelectedItemProps({
                            index: selectedItemsToShow.length,
                            role: "option",
                            selectedItem: undefined as unknown as Option,
                            id: `${chipIdPrefix}_${selectedItemsToShow.length}`,
                            onClick: onChipClick ? onChipClick : stopPropagationHandler,
                        })}
                        size={selectV2Theme.chip.size[inputFieldHeight](themeMode)}
                        overides={{ removeButton: { role: "note" } }}
                    >
                        {i18n._(
                            /*i18n*/ {
                                comment: "This is showing the number of selected items",
                                id: `{selectedValue} selected`,
                                values: { selectedValue },
                            }
                        )}
                    </Chip>
                ))}
            {children}
        </MultiSelectValueContainerStyled>
    );
};
